import { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Shield, Phone, Users, HelpCircle, BookOpen, Heart } from "lucide-react";
import { Button } from "./ui/button";

const NAV_ITEMS = [
  {
    label: "Hilfe holen",
    items: [
      { href: "/melden", label: "Jetzt melden", icon: Shield, highlight: true },
      { href: "/was-passiert", label: "Was passiert mit meiner Meldung?", icon: HelpCircle },
      { href: "/notfall", label: "Notfall-Hotlines", icon: Phone },
    ]
  },
  {
    label: "Informieren",
    items: [
      { href: "/faq", label: "Häufige Fragen (FAQ)", icon: HelpCircle },
      { href: "/selbstschutz", label: "Tipps zum Selbstschutz", icon: Shield },
      { href: "/erfahrungen", label: "Erfahrungsberichte", icon: Heart },
    ]
  },
  {
    label: "Für Erwachsene",
    items: [
      { href: "/eltern", label: "Für Eltern", icon: Users },
      { href: "/lehrer", label: "Für Lehrer", icon: BookOpen },
    ]
  },
  {
    label: "Über uns",
    href: "/ueber-uns",
  },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const closeTimer = useRef(null);

  const openMenu = (index) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(index);
  };

  const closeMenu = () => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 120);
  };
  const location = useLocation();

  const isActive = (href) => location.pathname === href;

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <nav className="px-4 md:px-8 lg:px-12 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <img 
              src="/logo.png" 
              alt="mobbing-melden.de Logo" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item, index) => (
              item.items ? (
                <div 
                  key={index}
                  className="relative"
                  onMouseEnter={() => openMenu(index)}
                  onMouseLeave={closeMenu}
                >
                  <button className="flex items-center gap-1 px-4 py-2 text-[#718096] hover:text-[#2D3748] font-medium transition-colors rounded-lg hover:bg-gray-50">
                    {item.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === index ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {openDropdown === index && (
                    <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 animate-fade-in-up" onMouseEnter={() => openMenu(index)} onMouseLeave={closeMenu}>
                      {item.items.map((subItem, subIndex) => {
                        const Icon = subItem.icon;
                        return (
                          <Link
                            key={subIndex}
                            to={subItem.href}
                            className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                              subItem.highlight ? 'bg-[#A0E8AF]/10 text-[#1A4731] font-semibold' : 'text-[#718096]'
                            } ${isActive(subItem.href) ? 'bg-[#A0E8AF]/20' : ''}`}
                          >
                            <Icon className={`w-5 h-5 ${subItem.highlight ? 'text-[#A0E8AF]' : ''}`} />
                            {subItem.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={index}
                  to={item.href}
                  className={`px-4 py-2 font-medium transition-colors rounded-lg hover:bg-gray-50 ${
                    isActive(item.href) ? 'text-[#1A4731] bg-[#A0E8AF]/20' : 'text-[#718096] hover:text-[#2D3748]'
                  }`}
                >
                  {item.label}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button Desktop */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/notfall">
              <Button variant="ghost" className="text-[#FC8181] hover:text-[#E53E3E] hover:bg-red-50 rounded-full">
                <Phone className="w-4 h-4 mr-2" />
                Notfall
              </Button>
            </Link>
            <Link to="/melden">
              <Button className="bg-[#A0E8AF] text-[#1A4731] hover:bg-[#86D696] rounded-full px-6 font-bold">
                Jetzt melden
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-[#718096] hover:text-[#2D3748] hover:bg-gray-100 rounded-lg"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-100 pt-4 animate-fade-in-up">
            {NAV_ITEMS.map((item, index) => (
              <div key={index} className="mb-2">
                {item.items ? (
                  <>
                    <p className="px-4 py-2 text-xs font-semibold text-[#A0AEC0] uppercase tracking-wider">
                      {item.label}
                    </p>
                    {item.items.map((subItem, subIndex) => {
                      const Icon = subItem.icon;
                      return (
                        <Link
                          key={subIndex}
                          to={subItem.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg mx-2 ${
                            subItem.highlight 
                              ? 'bg-[#A0E8AF]/10 text-[#1A4731] font-semibold' 
                              : 'text-[#718096] hover:bg-gray-50'
                          }`}
                        >
                          <Icon className={`w-5 h-5 ${subItem.highlight ? 'text-[#A0E8AF]' : ''}`} />
                          {subItem.label}
                        </Link>
                      );
                    })}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-3 text-[#718096] hover:text-[#2D3748] hover:bg-gray-50 rounded-lg mx-2"
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
            
            <div className="mt-4 px-4 space-y-2">
              <Link to="/notfall" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full justify-center text-[#FC8181] border-[#FC8181] hover:bg-red-50 rounded-full">
                  <Phone className="w-4 h-4 mr-2" />
                  Notfall-Hotlines
                </Button>
              </Link>
              <Link to="/melden" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full justify-center bg-[#A0E8AF] text-[#1A4731] hover:bg-[#86D696] rounded-full font-bold">
                  Jetzt melden
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
